import { Button, Grid, TextField } from '@mui/material';
import React, { MouseEvent, useEffect, useState } from 'react';
import './EmailSignature.scss';
import { useDispatch, useSelector } from 'react-redux';
import CustomModal from '../CustomModal/CustomModal';
import { showLoading } from '../LoadingView/reducers/LoadingViewReducer';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import { EmailSignatureSelector, resetSelectedSignature, setDefaultSignature, setDefaultEvoSignature, setEmailSignatures, setIsShowEmailSignatureEditor, setName, setSelectedItem, setSignature, setTriggerSearch } from './reducers/EmailSignatureReducer';
import AddSignatureButton from '../EmailEditorModal/AddSignatureButton';
import EmailSignatureApi, { DeleteEmailSignatureParam, SaveEmailSignatureParam, SetDefaultSignatureParam } from './api/EmailSignatureApi';
import Utility from '../../app/utility';
import BaseResponse from '../../app/BaseResponse';
import DatatableColumnModel from '../Datatable/DatatableColumnModel';
import EmailSignatureModel from './models/EmailSignatureModel';
import Datatable, { PagingTypeEnum } from '../Datatable/Datatable';
import _ from 'lodash';
import AddBannerButton from '../EmailEditorModal/AddBannerButton';
import JoditReact from "jodit-react-ts";
import 'jodit/es2021.en/jodit.css';
import { setDefaultSignatureId, setDefaultEvoSignatureId, userProfileSelector } from '../Login/reducers/UserProfileReducer';

interface EmailSignatureProp {
    system: string,
}


const EmailSignature: React.FunctionComponent<EmailSignatureProp> = (props) => {
    const dispatch = useDispatch();
    const emailSignatureData = useSelector(EmailSignatureSelector);
    const [selectedSignature, setSelectedSignature] = useState<string>("");
    const userProfile = useSelector(userProfileSelector);

    useEffect(() => {
        dispatch(showLoading(true));

        EmailSignatureApi.GetAll()
            .then((response: BaseResponse<EmailSignatureModel[]>) => {
                if (response.success) {
                    dispatch(setEmailSignatures(response.data));

                    let defaultSignature = _.find(response.data, (signature) => {
                        return userProfile.defaultSignatureId === signature.id
                    })

                    if (defaultSignature) {
                        dispatch(setDefaultSignature(defaultSignature));
                    }

                    let defaultEvoSignature = _.find(response.data, (signature) => {
                        return userProfile.defaultSignatureEvoId === signature.id
                    })

                    if (defaultEvoSignature) {
                        dispatch(setDefaultEvoSignature(defaultEvoSignature));
                    }
                } else {
                    Utility.Alert.Error(response.message);
                }
            }).catch((error) => {
                Utility.Alert.Error(error);
            })
            .finally(() => {
                dispatch(showLoading(false));
            })
    }, [emailSignatureData.triggerSearch])

    const columns = [
        {
            Field: "name",
            Title: "Name",
            AllowSort: false,
        } as DatatableColumnModel<EmailSignatureModel>,
        {
            Field: "signature",
            Title: "Signature",
            AllowSort: false,
            Render: (rowData: EmailSignatureModel) => (
                <div dangerouslySetInnerHTML={{ __html: rowData.signature }} />
            )
        } as DatatableColumnModel<EmailSignatureModel>,
        {
            Field: "isDefault",
            Title: "Default Signature",
            AllowSort: false,
            Render: (rowData: EmailSignatureModel) => (
                <>
                    {
                        props.system === "AscentBulk"? 
                            (rowData.id === userProfile.defaultSignatureId ? "Yes" : "No"): 
                            (rowData.id === userProfile.defaultSignatureEvoId ? "Yes" : "No")
                    }
                </>
            )
        } as DatatableColumnModel<EmailSignatureModel>,
        {
            Field: "",
            Title: "",
            AllowSort: false,
            Render: (rowData: EmailSignatureModel) => (
                <>
                    <Button
                        onClick={() => {
                            EmailSignatureApi.SetDefaultSignature({
                                id: rowData.id,
                                system: props.system,
                            } as SetDefaultSignatureParam)
                                .then((response: BaseResponse<EmailSignatureModel>) => {
                                    if (response.success) {
                                        Utility.Alert.Success("Set default signature successful");

                                        if (props.system === "AscentBulk") {
                                            dispatch(setDefaultSignatureId(rowData.id))
                                        } else {
                                            dispatch(setDefaultEvoSignatureId(rowData.id))
                                        }
                                        dispatch(setSignature(rowData.signature))

                                        dispatch(setTriggerSearch(new Date().getTime()));
                                    } else {
                                        dispatch(showLoading(false));
                                        Utility.Alert.Error(response.message);
                                    }
                                })
                                .catch((error) => {
                                    dispatch(showLoading(false));
                                    Utility.Alert.Error(error);
                                });
                        }}
                        variant='outlined'
                    >Set Default</Button>
                </>
            )
        } as DatatableColumnModel<EmailSignatureModel>,
    ];

    const saveSignature = () => {
        try {
            validate()
            EmailSignatureApi.Save({
                id: emailSignatureData.selectedItem.id,
                name: emailSignatureData.selectedItem.name,
                signature: emailSignatureData.selectedItem.signature,
                system: props.system,
            } as SaveEmailSignatureParam)
                .then((response: BaseResponse<EmailSignatureModel>) => {
                    if (response.success) {
                        Utility.Alert.Success("Update signature successful");

                        dispatch(setTriggerSearch(new Date().getTime()));
                    } else {
                        dispatch(showLoading(false));
                        Utility.Alert.Error(response.message);
                    }
                })
                .catch((error) => {
                    dispatch(showLoading(false));
                    Utility.Alert.Error(error);
                });
        } catch (error) {
            Utility.Alert.Error(error);
        }
    }

    const validate = () => {
        if (emailSignatureData.selectedItem.name.length === 0) {
            throw "Name is required";
        }

        if (emailSignatureData.selectedItem.signature.length === 0) {
            throw "Signature is required";
        }
    }

    const getSignaturesBySystem = () => {
        return _.filter(emailSignatureData.signatures, (signature) => {
            return signature.system === props.system;
        });
    }

    return (
        <CustomModal
            open={emailSignatureData.isShowEmailSignatureEditor}
            onClose={() => {
                dispatch(setIsShowEmailSignatureEditor(false));
            }}
            className='email-signature-modal nice-scroll'
        >
            <Grid container>
                <Grid xs={5} item>
                    <Grid container>
                        <Grid xs={10} item>
                            <Button
                                fullWidth
                                variant='contained'
                                className="signature-input-component"
                                color='primary'
                                onClick={() => {
                                    saveSignature();
                                }}
                            >SAVE SIGNATURE</Button>
                        </Grid>
                        <Grid xs={2} item>
                            <Button
                                className="clear-input-button"
                                tabIndex={-1}
                                color="success"
                                variant="contained"
                                onClick={() => {
                                    dispatch(resetSelectedSignature())
                                }}
                            >
                                <i className="fa fa-refresh" aria-hidden="true"></i>
                            </Button>
                        </Grid>
                    </Grid>
                    <TextField
                        fullWidth
                        className="signature-input-component"
                        label="Name"
                        value={emailSignatureData.selectedItem.name}
                        onChange={(event) => {
                            dispatch(setName(event.target.value));
                        }}
                    >
                    </TextField>
                    <br />
                    {props.system === "AscentBulk" &&
                        <div>
                            <AddSignatureButton signatureValue={emailSignatureData.selectedItem.signature} onClick={(signature: string) => {
                                dispatch(setSignature(signature));
                                setSelectedSignature(signature);
                            }} />,
                            <AddBannerButton signatureValue={emailSignatureData.selectedItem.signature} onClick={(signature: string) => {
                                dispatch(setSignature(signature));
                                setSelectedSignature(signature);
                            }} />
                        </div>
                    }
                    <br />
                    <JoditReact onChange={(content) => { dispatch(setSignature(content)) }} defaultValue={selectedSignature} />
                </Grid>
                <Grid xs={7} item className='signature-table'>
                    <Datatable<EmailSignatureModel>
                        useDefaultSearchBar={false}
                        pagingType={PagingTypeEnum.None}
                        data={getSignaturesBySystem()}
                        useItemPerPage={false}
                        columns={columns}
                        isDataLoaded={true}
                        useHeaderSort={true}
                        stickyHeader={true}
                        onRowSelectedChange={(selectedRow: EmailSignatureModel, selectedIndex: number, event: MouseEvent<HTMLElement>) => {
                            var target = event.target as HTMLElement;
                            if (target.tagName !== "BUTTON") {
                                dispatch(setSelectedItem(selectedRow));
                                setSelectedSignature(selectedRow.signature);
                            }
                        }}
                    ></Datatable>
                </Grid>
            </Grid>
        </CustomModal>
    )
};

export default EmailSignature;